import React from 'react';
import { Box, CircularProgress, Popover, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { getApiErrorMessage } from '../../utils/apiError';
import { shortName } from '../../utils/productShortName';
import { colors } from '../../themes/typography';
import ProductMachinesPopover from '../POSAnalysis/ProductMachinesPopover';

const perDay = (value, lang) => (value ?? 0).toLocaleString(lang, { maximumFractionDigits: 1 });

/**
 * "Probar en N máquinas": a swap that paid off, tried in more machines with the same popover as Top Productos by
 * machine — its candidate machines (where the product it replaced sells least first) with the tested product coming in
 * and the lane the test would take marked, the planogram on the side and one start and length for all of them. What it creates is accepted and tagged as AI, and the table refreshes.
 */
const ReplicatePopover = ({ record, anchorEl, customerId, baseline, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const [plan, setPlan] = React.useState({ status: 'loading', data: null });
  const [allProducts, setAllProducts] = React.useState(null);
  const created = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    const headers = createAuthHeader(localStorage.getItem('access_token'));
    axios.get(`${API_BASE_URL}/experiments/${record.id}/replication?clientId=${customerId}&baseline=${baseline}`, { headers })
      .then(({ data }) => { if (!cancelled) setPlan({ status: 'ready', data }); })
      .catch((error) => {
        if (cancelled) return;
        // 404: since the table loaded, the swap lost its room (new tests, or the machines took the product)
        const stale = error?.response?.status === 404;
        const message = stale ? t('rp_stale') : !error?.response ? t('rp_offline') : getApiErrorMessage(error);
        setPlan({ status: 'error', data: message, stale });
      });
    axios.get(`${API_BASE_URL}/all-products?clientId=${encodeURIComponent(customerId)}`, { headers })
      .then((resp) => { if (!cancelled) setAllProducts(resp.data?.products || []); })
      .catch(() => { if (!cancelled) setAllProducts([]); });
    return () => { cancelled = true; };
  }, [record.id, customerId, baseline]); // eslint-disable-line react-hooks/exhaustive-deps

  // The table shows the new accepted experiments and drops these machines from the link, or the link gone stale
  const close = () => {
    if (created.current || plan.stale) window.dispatchEvent(new CustomEvent('experimentMutation'));
    onClose();
  };

  if (plan.status !== 'ready') {
    return (
      <Popover
        open
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, p: 1.5, borderRadius: '8px', border: `1px solid ${colors.border.light}` } } }}
      >
        {plan.status === 'loading'
          ? <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
          : <Typography sx={{ fontSize: '12px', color: plan.stale ? colors.text.secondary : 'error.main' }}>{plan.data}</Typography>}
      </Popover>
    );
  }

  const data = plan.data;
  const fromName = shortName(data.from.name);
  const toName = shortName(data.to.name);
  const machines = data.machines.filter((m) => m.proposedLane);
  return (
    <ProductMachinesPopover
      anchorEl={anchorEl}
      onClose={close}
      clientId={customerId}
      scope="sector"
      scopeId={data.sector}
      product={{ id: data.from.id, name: data.from.name }}
      allProducts={allProducts}
      machineIds={machines.map((m) => m.dStoreId)}
      incoming={{ id: data.to.id, name: data.to.name }}
      initialLanes={Object.fromEntries(machines.map((m) => [m.dStoreId, [m.proposedLane]]))}
      title={<>{t('rp_titleTry')} <Box component="span" sx={{ color: '#16a34a' }}>{toName}</Box> {t('rp_titleInsteadOf')} {fromName}</>}
      subtitle={[
        `${data.wins}/${data.tests} ${t('rp_statWins')}`,
        data.to.unitsPerDayInTest != null && `${t('rp_perDay', { value: perDay(data.to.unitsPerDayInTest, lang) })} ${t('rp_statTest')}`,
        data.to.sectorMachines > 0 && `${t('rp_perDay', { value: perDay(data.to.sectorUnitsPerDay, lang) })} ${t('rp_statSector', { count: data.to.sectorMachines })}`,
      ].filter(Boolean).join(' · ')}
      isAi
      opensLeft
      onCreated={() => { created.current = true; }}
    />
  );
};

export default ReplicatePopover;
